import { TimePicker as AntdTimePicker } from "antd";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import type { FocusEvent } from "react";

interface TimePickerProps {
  name: string;
  onChange: (
    name: string,
    values: { timeString: string; ISOString: string | null },
  ) => void;
  value?: string | null;
  placeholder: string;
  extraLabel?: boolean;
  emphasized?: boolean;
  minimumHour?: number;
  needConfirm?: boolean;
  showColumnLabels?: boolean;
}

const TimePicker = ({
  onChange,
  name,
  value,
  placeholder,
  extraLabel,
  emphasized,
  minimumHour,
  needConfirm,
  showColumnLabels,
}: TimePickerProps) => {
  const changeHandler = (
    time: Dayjs | null,
    timeString: string | string[],
  ) => {
    if (time && minimumHour != null && time.hour() < minimumHour) {
      onChange(name, { ISOString: null, timeString: "" });
      return;
    }

    onChange(name, {
      ISOString: time?.toISOString() || null,
      timeString: Array.isArray(timeString) ? timeString[0] : timeString,
    });
  };

  const blurHandler = (event: FocusEvent<HTMLElement>) => {
    // Ant Design does not emit onChange when a masked time input is emptied
    // with Backspace/Delete. Propagate the clear explicitly when focus leaves.
    if (
      event.target instanceof HTMLInputElement &&
      !event.target.value.trim()
    ) {
      onChange(name, { ISOString: null, timeString: "" });
    }
  };

  return (
    <div
      className={`w-full flex flex-col justify-start items-start ${
        emphasized ? "gap-2" : "gap-1"
      }`}
    >
      {extraLabel ? (
        <div
          className={`whitespace-nowrap w-auto ${
            emphasized
              ? "text-xs font-semibold text-slate-700"
              : "text-[10px]"
          }`}
        >
          {placeholder}
        </div>
      ) : (
        <></>
      )}
      <AntdTimePicker
        direction="rtl"
        value={value ? dayjs(value) : null}
        onChange={changeHandler}
        onBlur={blurHandler}
        format={["HH:mm", "H:mm"]}
        disabledTime={
          minimumHour == null
            ? undefined
            : () => ({
                disabledHours: () =>
                  Array.from({ length: minimumHour }, (_, hour) => hour),
              })
        }
        hideDisabledOptions={minimumHour != null}
        showNow={minimumHour == null}
        needConfirm={needConfirm}
        classNames={{
          popup: {
            root: showColumnLabels ? "time-picker-labeled-columns" : "",
          },
        }}
        panelRender={
          showColumnLabels
            ? (panel) => (
                <div className="time-picker-labeled-panel">
                  <div className="time-picker-column-labels">
                    <span>ساعت</span>
                    <span>دقیقه</span>
                  </div>
                  {panel}
                </div>
              )
            : undefined
        }
        className={
          emphasized
            ? "!h-11 !rounded-lg !border-slate-300 !bg-white !px-3 hover:!border-sky-500 focus-within:!border-sky-500 [&_.ant-picker-input>input]:!text-sm [&_.ant-picker-input>input]:!font-semibold [&_.ant-picker-input>input]:!text-slate-800 [&_.ant-picker-input>input::placeholder]:!font-normal [&_.ant-picker-input>input::placeholder]:!text-slate-500 [&_.ant-picker-suffix]:!text-slate-500"
            : undefined
        }
        style={{ width: "100%" }}
        placeholder={placeholder}
      />
    </div>
  );
};

export default TimePicker;
